import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House, ChartBar, Shrimp, ClipboardText, Package, Scales,
  CurrencyDollar, Brain, Users, SignOut, List, X, CaretDown, ArrowsClockwise,
  ClockCounterClockwise, ChartLine, Buildings, CalendarCheck, Receipt, ShoppingCart, Warning,
  BookOpen, PiggyBank
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import NetworkStatus from "@/components/NetworkStatus";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";

const ROLE_LABELS = {
  admin: "Administrator",
  manager: "Manager",
  pengurus: "Pengurus",
  korwil: "KORWIL",
  korsub: "KORSUB",
  asisten_supervisor: "Asisten Supervisor",
};

const FIELD_ROLES = ["asisten_supervisor", "korsub", "korwil"];

const navItems = [
  { to: "/", icon: House, label: "Dashboard", roles: "all" },
  { to: "/laporan", icon: ClipboardText, label: "Laporan Produksi", roles: ["admin", "manager", "pengurus"] },
  { to: "/tambak", icon: Shrimp, label: "Manajemen Tambak", roles: "all" },
  { to: "/siklus", icon: ArrowsClockwise, label: "Siklus Budidaya", roles: "all" },
  { to: "/jadwal", icon: CalendarCheck, label: "Jadwal Kegiatan", roles: "all" },
  { to: "/gudang", icon: Package, label: "Gudang", roles: ["admin", "manager", "pengurus"] },
  { to: "/purchase-orders", icon: ShoppingCart, label: "Purchase Order", roles: ["admin", "manager", "pengurus"] },
  { to: "/panen", icon: Scales, label: "Panen & Penjualan", roles: ["admin", "manager", "pengurus"] },
  { to: "/invoices", icon: Receipt, label: "Invoice", roles: ["admin", "manager", "pengurus"] },
  { to: "/keuangan", icon: CurrencyDollar, label: "Keuangan", roles: ["admin", "manager", "pengurus"] },
  { to: "/akuntansi", icon: BookOpen, label: "Akuntansi", roles: ["admin", "manager", "pengurus"] },
  { to: "/simpanan", icon: PiggyBank, label: "Simpanan Anggota", roles: ["admin", "manager", "pengurus"] },
  { to: "/laba-rugi", icon: ChartLine, label: "Laba / Rugi", roles: ["admin", "manager", "pengurus"] },
  { to: "/analytics", icon: ChartBar, label: "Analytics Tambak", roles: ["admin", "manager", "pengurus"] },
  { to: "/ai-analisis", icon: Brain, label: "AI Analisis", roles: ["admin", "manager", "pengurus"] },
  { to: "/pengguna", icon: Users, label: "Manajemen Pengguna", roles: ["admin", "manager"], permission: "can_manage_users" },
  { to: "/audit-log", icon: ClockCounterClockwise, label: "Audit Trail", roles: ["admin", "manager", "pengurus"] },
  { to: "/profil", icon: Buildings, label: "Profil Koperasi", roles: ["admin", "manager", "pengurus"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filteredNav = navItems.filter(
    (item) => {
      if (item.roles === "all") return true;
      if (item.permission && user?.[item.permission]) return true;
      return item.roles?.includes(user?.role);
    }
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAFAFA]">
      <NetworkStatus />
      <InstallPrompt />
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[260px] sm:w-64 bg-[#1A1A1A] text-white
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          flex flex-col`}
      >
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <img src="/logo-p3uw.png" alt="P3UW" className="w-9 h-9 rounded-lg object-cover" />
          <div>
            <h1 className="text-base font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
              P3UW
            </h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em]">
              KPBD
            </p>
          </div>
          <button
            className="ml-auto lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {filteredNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200
                ${isActive
                  ? "bg-[#B8860B]/20 text-[#F5C842] font-medium"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon size={20} weight="duotone" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-white/5 transition-all duration-200"
          >
            <SignOut size={20} />
            Keluar
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header
          data-testid="topbar"
          className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between"
        >
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600"
            onClick={() => setSidebarOpen(true)}
          >
            <List size={22} />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell />
            <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-md bg-[#B8860B]/10 text-[#B8860B] text-xs font-semibold uppercase tracking-wider">
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 h-auto"
                >
                  <div className="w-8 h-8 rounded-full bg-[#B8860B] flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden sm:block text-sm text-slate-700 font-medium">
                    {user?.name}
                  </span>
                  <CaretDown size={14} className="text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-slate-500">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="dropdown-logout"
                  onClick={handleLogout}
                  className="text-red-600"
                >
                  <SignOut size={16} className="mr-2" />
                  Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
